import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Gear, X, CurrencyDollar, ChartLine, Receipt, Money, Recycle, Scales,
  Stack, Users, Truck, Clock, ListChecks, Warning, Buildings, ArrowsLeftRight,
} from "@phosphor-icons/react";

/**
 * KPI LIBRARY — single source of truth for every dashboard metric admins
 * can swap into a card. Each entry resolves a value from the analytics
 * `metrics` payload and formats it for display.
 *
 * Layout / sizing rules:
 *   - The dashboard layout itself is FROZEN. This file only swaps the
 *     metric inside a card — never the card's position, size, or colors.
 *   - `value` is responsible for returning the formatted display string.
 *   - `accent` mirrors the prior dashboard's coloring convention so
 *     financial cards keep the gold-on-navy look.
 *
 * To add a new KPI: append an entry to KPI_LIBRARY with a unique `key`
 * plus a `value(metrics, ctx)` resolver. It will automatically appear in
 * the KPI picker grouped under its `category`.
 */

const fmt$ = (n) => (n == null || isNaN(Number(n)))
  ? "—"
  : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtNum = (n, decimals = 0) => (n == null || isNaN(Number(n)))
  ? "—"
  : Number(n).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const fmtPct = (n) => (n == null || isNaN(Number(n))) ? "—" : `${Number(n).toFixed(1)}%`;

const fmtHoursDash = (hrs) => {
  const v = Number(hrs);
  if (!isFinite(v) || v <= 0) return "0s";
  if (v < 1 / 60) return `${Math.round(v * 3600)}s`;
  const h = Math.floor(v);
  const m = Math.round((v - h) * 60);
  return h === 0 ? `${m}m` : `${h}h ${m}m`;
};

export const KPI_LIBRARY = [
  // ── Financial ────────────────────────────────────────────────
  {
    key: "revenue", category: "Financial", label: "Revenue", short: "Revenue",
    Icon: CurrencyDollar, accent: true,
    value: (m) => fmt$(m.revenue),
  },
  {
    key: "profit", category: "Financial", label: "Profit", short: "Profit",
    Icon: ChartLine, accent: true,
    value: (m) => fmt$(m.profit),
    positive: (m) => m.profit != null ? Number(m.profit) >= 0 : null,
  },
  {
    key: "disposal_cost", category: "Financial", label: "Est. Disposal", short: "Est. Disp",
    Icon: Receipt,
    value: (m) => fmt$(m.estimated_disposal_cost),
    hint: "Estimated from container yards × tons/yd³ × tipping rate. Compare to Actual Disposal to spot pricing drift.",
  },
  {
    key: "actual_disposal_cost", category: "Financial", label: "Actual Disposal", short: "Act. Disp",
    Icon: Receipt, accent: true,
    value: (m) => fmt$(m.actual_disposal_cost),
    hint: "Sum of the disposal tickets logged for this period. Updates instantly when you log a new ticket in Disposal Tickets.",
  },
  {
    key: "disposal_per_ton", category: "Financial", label: "Disposal $/Ton", short: "$/Ton",
    Icon: Receipt,
    value: (m, ctx) => fmt$(ctx?.analytics?.settings?.disposal_cost_per_ton ?? ctx?.analytics?.tipping_rate),
  },
  {
    key: "revenue_per_yard", category: "Financial", label: "Revenue / Yard", short: "Rev/Yd",
    Icon: Money,
    value: (m) => fmt$(m.avg_revenue_per_yard),
  },
  {
    key: "profit_per_yard", category: "Financial", label: "Profit / Yard", short: "Profit/Yd",
    Icon: Money,
    value: (m) => fmt$(m.profit_per_yard),
    positive: (m) => m.profit_per_yard != null ? Number(m.profit_per_yard) >= 0 : null,
  },
  {
    key: "revenue_per_customer", category: "Financial", label: "Revenue / Customer", short: "Rev/Cust",
    Icon: Money,
    value: (m) => fmt$(m.revenue_per_customer),
  },
  {
    key: "avg_revenue_per_stop", category: "Financial", label: "Avg Rev / Stop", short: "Rev/Stop",
    Icon: Money,
    value: (m) => fmt$(m.avg_revenue_per_stop),
  },

  // ── Operations ───────────────────────────────────────────────
  {
    key: "stops", category: "Operations", label: "Stops", short: "Stops",
    Icon: Stack,
    value: (m) => fmtNum(m.total_pickups, 0),
  },
  {
    key: "completed_stops", category: "Operations", label: "Completed Stops", short: "Done",
    Icon: ListChecks,
    value: (m) => fmtNum(m.completed_stops, 0),
  },
  {
    key: "missed_stops", category: "Operations", label: "Missed Stops", short: "Missed",
    Icon: Warning,
    value: (m) => fmtNum(m.missed_stops, 0),
  },
  {
    key: "routes", category: "Operations", label: "Routes", short: "Routes",
    Icon: Truck,
    value: (m) => fmtNum(m.routes_count, 0),
  },
  {
    key: "route_hours", category: "Operations", label: "Route Hours", short: "Hours",
    Icon: Clock, accent: true,
    value: (m, ctx) => fmtHoursDash(m.total_route_hours),
    sub: (m, ctx) => ctx?.analytics?.sessions ? `${ctx.analytics.sessions.length} sessions` : "—",
  },

  // ── Customers ────────────────────────────────────────────────
  {
    key: "customers", category: "Customers", label: "Total Customers", short: "Customers",
    Icon: Users,
    value: (m) => fmtNum(m.total_customers, 0),
  },
  {
    key: "new_customers", category: "Customers", label: "New Customers", short: "New",
    Icon: Users,
    value: (m) => fmtNum(m.new_customers, 0),
  },
  {
    key: "customer_growth", category: "Customers", label: "Customer Growth", short: "Growth",
    Icon: ChartLine,
    value: (m) => fmtPct(m.customer_growth_pct),
    positive: (m) => m.customer_growth_pct != null ? Number(m.customer_growth_pct) >= 0 : null,
  },

  // ── Containers ───────────────────────────────────────────────
  {
    key: "containers", category: "Containers", label: "Total Containers", short: "Containers",
    Icon: Truck,
    value: (m) => fmtNum(m.total_containers, 0),
  },
  {
    key: "containers_2yd", category: "Containers", label: "2 Yard Containers", short: "2yd",
    Icon: Truck,
    value: (m) => fmtNum(m.containers_2yd, 0),
  },
  {
    key: "containers_4yd", category: "Containers", label: "4 Yard Containers", short: "4yd",
    Icon: Truck,
    value: (m) => fmtNum(m.containers_4yd, 0),
  },
  {
    key: "containers_6yd", category: "Containers", label: "6 Yard Containers", short: "6yd",
    Icon: Truck,
    value: (m) => fmtNum(m.containers_6yd, 0),
  },
  {
    key: "containers_8yd", category: "Containers", label: "8 Yard Containers", short: "8yd",
    Icon: Truck,
    value: (m) => fmtNum(m.containers_8yd, 0),
  },
  {
    key: "containers_compactor", category: "Containers", label: "Compactors", short: "Compactors",
    Icon: Truck,
    value: (m) => fmtNum(m.containers_compactor, 0),
  },
  {
    key: "containers_recycling", category: "Containers", label: "Recycling Containers", short: "Recycle",
    Icon: Recycle,
    value: (m) => fmtNum(m.containers_recycling, 0),
  },
  {
    key: "containers_cardboard", category: "Containers", label: "Cardboard Containers", short: "Cardboard",
    Icon: Recycle,
    value: (m) => fmtNum(m.containers_cardboard, 0),
  },

  // ── Yardage ──────────────────────────────────────────────────
  {
    key: "yards_serviced", category: "Yardage", label: "Yards Serviced", short: "Yards",
    Icon: Recycle,
    value: (m) => fmtNum(m.yards_serviced, 1),
    sub: () => "yd³",
  },
  {
    key: "avg_yards_per_stop", category: "Yardage", label: "Avg Yards / Stop", short: "Yd/Stop",
    Icon: Recycle,
    value: (m) => fmtNum(m.avg_yards_per_stop, 2),
  },
  {
    key: "avg_yards_per_customer", category: "Yardage", label: "Avg Yards / Customer", short: "Yd/Cust",
    Icon: Recycle,
    value: (m) => fmtNum(m.avg_yards_per_customer, 2),
  },
  {
    key: "estimated_tonnage", category: "Yardage", label: "Estimated Tonnage", short: "Tons",
    Icon: Scales,
    value: (m) => fmtNum(m.estimated_tonnage, 2),
    sub: (m) => `${fmtNum((m.estimated_tonnage || 0) * 2000, 0)} lbs`,
  },
];

export const KPI_BY_KEY = Object.fromEntries(KPI_LIBRARY.map((k) => [k.key, k]));

export const KPI_CATEGORIES = ["Financial", "Operations", "Customers", "Containers", "Yardage"];

/**
 * KPI picker popover — opens when the user clicks the gear icon on a card.
 * Renders categorized list of every metric in KPI_LIBRARY plus an option
 * to reset the slot to its default.
 *
 *   anchorRef:    DOM ref the popover positions next to (right-aligned).
 *   currentKey:   the KPI key currently displayed in the card.
 *   defaultKey:   the dashboard's default KPI for this slot — shown so the
 *                 admin can revert easily.
 *   onPick:       (newKey: string | null) => void. null === reset to default.
 *   onClose:      called when the picker should be dismissed.
 */
export function KpiPicker({ anchorRef, currentKey, defaultKey, onPick, onClose }) {
  const popRef = useRef(null);
  useEffect(() => {
    function onDocClick(e) {
      if (popRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose();
    }
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchorRef, onClose]);

  const defaultMeta = defaultKey ? KPI_BY_KEY[defaultKey] : null;

  return (
    <div
      ref={popRef}
      className="absolute right-0 top-full mt-1 z-30 w-80 max-h-96 overflow-y-auto bg-white border border-[#012340]/15 rounded-sm shadow-xl"
      data-testid="kpi-picker"
      onClick={(e) => e.stopPropagation()}
    >
      <header className="px-3 py-2 border-b border-[#012340]/10 flex items-center justify-between sticky top-0 bg-white">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C738A] font-bold">Pick a metric</div>
        <button
          onClick={onClose}
          className="p-1 rounded-sm hover:bg-[#012340]/5"
          aria-label="Close"
          data-testid="kpi-picker-close"
        >
          <X size={14} weight="bold" />
        </button>
      </header>

      {defaultMeta && defaultKey !== currentKey && (
        <button
          type="button"
          onClick={() => onPick(null)}
          className="w-full text-left px-3 py-2 border-b border-[#012340]/10 hover:bg-[#FAFAFA] flex items-center gap-2"
          data-testid="kpi-picker-reset"
        >
          <ArrowsLeftRight size={14} weight="bold" className="text-[#CFAB7A]" />
          <span className="text-xs font-bold text-[#012340]">Reset to default · {defaultMeta.label}</span>
        </button>
      )}

      {KPI_CATEGORIES.map((cat) => {
        const items = KPI_LIBRARY.filter((k) => k.category === cat);
        if (!items.length) return null;
        return (
          <div key={cat}>
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-bold text-[#5C738A] bg-[#FAFAFA] sticky top-[37px]">
              {cat}
            </div>
            {items.map((k) => {
              const Icon = k.Icon;
              const isCurrent = k.key === currentKey;
              return (
                <button
                  type="button"
                  key={k.key}
                  onClick={() => onPick(k.key)}
                  data-testid={`kpi-picker-option-${k.key}`}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-[#FAFAFA] ${isCurrent ? "bg-[#CFAB7A]/15" : ""}`}
                >
                  <Icon size={14} weight="bold" className={isCurrent ? "text-[#012340]" : "text-[#5C738A]"} />
                  <span className={`text-sm flex-1 ${isCurrent ? "font-bold text-[#012340]" : "text-[#012340]"}`}>{k.label}</span>
                  {isCurrent && <span className="text-[10px] uppercase tracking-[0.18em] text-[#CFAB7A] font-bold">Active</span>}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Wrapper around the existing dashboard Kpi card that:
 *   1. Looks up which metric to display from `kpiConfig[slotId]` (or falls back to `defaultKey`)
 *   2. Renders the actual KpiInner (the card visuals stay identical to before)
 *   3. Adds a hover-only gear icon that opens the KpiPicker
 *
 * Layout is unchanged — same card width/height/spacing/colors.
 */
export function ConfigurableKpi({
  slotId,
  defaultKey,
  metrics,
  analytics,
  kpiConfig,
  onChange,            // (slotId, newKey | null) => void
  onClick,             // forwarded to inner card (used by Revenue → breakdown modal)
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const wrapRef = useRef(null);
  const gearRef = useRef(null);

  const activeKey = kpiConfig?.[slotId] || defaultKey;
  const meta = KPI_BY_KEY[activeKey] || KPI_BY_KEY[defaultKey];
  if (!meta) return null;

  const ctx = { analytics };
  const value = meta.value(metrics, ctx);
  const sub = meta.sub ? meta.sub(metrics, ctx) : undefined;
  const positive = meta.positive ? meta.positive(metrics, ctx) : null;
  const Icon = meta.Icon;
  // The original Revenue card opens a breakdown modal on click. Only forward
  // that handler when the slot is still showing the Revenue KPI.
  const cardOnClick = activeKey === "revenue" ? onClick : undefined;

  return (
    <div ref={wrapRef} className="relative group" data-testid={`kpi-slot-${slotId}`}>
      <KpiInner
        label={meta.label}
        value={value}
        Icon={Icon}
        accent={meta.accent}
        positive={positive}
        sub={sub}
        onClick={cardOnClick}
        testid={`kpi-${meta.key}`}
      />
      <button
        ref={gearRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setPickerOpen((v) => !v); }}
        className="absolute top-2 right-2 p-1.5 rounded-sm bg-white/90 border border-[#012340]/15 text-[#5C738A] opacity-0 group-hover:opacity-100 transition-opacity hover:text-[#012340] hover:border-[#012340]/40 shadow-sm focus:opacity-100"
        aria-label="Change metric"
        data-testid={`kpi-gear-${slotId}`}
        title="Change metric"
      >
        <Gear size={12} weight="bold" />
      </button>
      {pickerOpen && (
        <KpiPicker
          anchorRef={gearRef}
          currentKey={activeKey}
          defaultKey={defaultKey}
          onPick={(newKey) => {
            onChange(slotId, newKey);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Inner card — purely visual. EXACTLY matches the existing dashboard Kpi
 * component (same Tailwind classes / shadows / spacing) so the layout
 * stays frozen — we only swap the metric inside.
 */
function KpiInner({ label, value, Icon, accent, positive, sub, onClick, testid }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      data-testid={testid || `kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`bg-white border border-[#012340]/10 rounded-sm p-5 w-full text-left ${onClick ? "hover:border-[#CFAB7A] hover:shadow-sm transition-all cursor-pointer" : ""}`}
    >
      <div className="flex items-start justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">{label}</div>
        <div className={`w-8 h-8 rounded-sm grid place-items-center ${accent ? "bg-[#CFAB7A] text-[#012340]" : "bg-[#012340] text-[#CFAB7A]"}`}>
          {Icon && <Icon size={16} weight="bold" />}
        </div>
      </div>
      <div className={`kpi-number text-3xl mt-3 ${positive === false ? "text-[#D32F2F]" : positive === true ? "text-[#2E7D32]" : "text-[#012340]"}`}>{value}</div>
      {sub && <div className="text-xs text-[#5C738A] mt-1">{sub}</div>}
    </Tag>
  );
}

/** Hook that loads + persists the per-user KPI config. */
export function useKpiConfig(api) {
  const [cfg, setCfg] = useState(() => {
    try {
      const raw = localStorage.getItem("zmex.dashboard.kpi_config");
      if (raw) return JSON.parse(raw) || {};
    } catch { /* ignore */ }
    return {};
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/kpi-config");
        if (cancelled) return;
        const next = data?.kpi_config || {};
        setCfg(next);
        try { localStorage.setItem("zmex.dashboard.kpi_config", JSON.stringify(next)); } catch { /* noop */ }
      } catch { /* offline / not signed in — keep cache */ }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const set = useMemo(
    () => (slotId, newKey) => {
      setCfg((prev) => {
        const next = { ...prev };
        if (newKey == null) delete next[slotId];
        else next[slotId] = newKey;
        try { localStorage.setItem("zmex.dashboard.kpi_config", JSON.stringify(next)); } catch { /* noop */ }
        // Fire-and-forget server save — UI is instant from localStorage.
        api.put("/me/kpi-config", { kpi_config: next }).catch(() => { /* surface later if needed */ });
        return next;
      });
    },
    [api]
  );

  return [cfg, set];
}
